/**
 * ONE READING OF THE CANCELLATION FEE SCHEDULE (2026-11-04).
 *
 * The policy modal is the only place the terms are written now — the summary page just asks the customer to
 * read and accept them — but the schedule itself is read in two places inside that modal (the tier list and the
 * per-tier labels), and it used to be re-implemented wherever a summary was needed.
 *
 * The owner, on where the wording belongs:
 *
 *   > "Lets move all the wordings to the modal and have like read and accept our Cancellation and Reschedule
 *   > Policy."
 *
 * Keeping the tier parsing here means the modal cannot disagree with itself about what a tier means, and any
 * future surface reads the same rules instead of growing a second copy.
 */

const DEFAULT_CUSTOM_FEE_TIERS = [
  { minHours: 0, feePercent: 100 },
  { minHours: 24, feePercent: 75 },
  { minHours: 48, feePercent: 50 },
  { minHours: 96, feePercent: 0 },
];

/** The fee tiers to display, in ascending notice, from whatever shape the setting is in. */
export function buildDisplayTiers(policyConfig) {
  const configuredTiers = Array.isArray(policyConfig?.feeTiers)
    ? [...policyConfig.feeTiers]
    : [];

  if (configuredTiers.length > 0) {
    return configuredTiers.sort((a, b) => (a.minHours || 0) - (b.minHours || 0));
  }

  if (!policyConfig?.enabled || policyConfig?.policyType === "free") {
    return [{ minHours: 0, feePercent: 0 }];
  }

  if (policyConfig?.policyType === "strict") {
    return [{ minHours: 0, feePercent: 100 }];
  }

  if (policyConfig?.policyType === "custom") {
    return DEFAULT_CUSTOM_FEE_TIERS;
  }

  const typeToHours = { "24-hour": 24, "48-hour": 48, "72-hour": 72 };
  const thresholdHours =
    (policyConfig?.policyType && typeToHours[policyConfig.policyType]) ||
    policyConfig?.hoursBeforeAppointment ||
    24;
  const lateFeePercent = policyConfig?.cancellationFeePercent ?? 50;

  return [
    { minHours: 0, feePercent: lateFeePercent },
    { minHours: thresholdHours, feePercent: 0 },
  ];
}

/** "Less than 24 hrs notice" / "48 - 96 hrs notice" / "More than 96 hrs notice". */
export function formatTierLabel(tier, index, feeTiers) {
  const min = Number(tier?.minHours) || 0;
  const next = feeTiers[index + 1];
  const nextMin = next ? Number(next.minHours) : null;

  if (feeTiers.length === 1 && min === 0) {
    return "Any time before appointment";
  }

  if (nextMin !== null) {
    if (min <= 0) return `Less than ${nextMin} hrs notice`;
    return `${min} - ${nextMin} hrs notice`;
  }
  if (min <= 0) return "Less than 24 hrs notice";
  return `More than ${min} hrs notice`;
}
